import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useToast } from '@chakra-ui/core'
import { Button, HorizontalScroller } from 'components/ui'
import { ChevronLeft, ChevronRight, Download, Maximize2, Plus, Trash2, MessageSquarePlus, Video } from 'lucide-react'
import { useExecutionText } from '../../hooks/useExecutionText'
import { mediaName, videoMedia } from '../../model/media'
import { downloadMedia } from '../../api/downloadMedia'
import { ExecutionMediaPreview } from '../ExecutionMediaPreview'
import { getAiScorePresentation } from 'components/TasksV2/model/aiScore'

export const MediaThumbnail: React.FC<{ url: string; mediaType?: 'image' | 'video' }> = ({ url, mediaType }) =>
  (mediaType ? mediaType === 'video' : videoMedia(url)) ? (
    <video src={mediaType ? `${url}#t=0.1` : url} muted playsInline preload="metadata" aria-hidden="true" />
  ) : (
    <img src={url} alt="" />
  )

interface IProps {
  media: string[]
  index: number
  onChange: (index: number) => void
  counts: number[]
  isRtl: boolean
  onAdd?: () => void
  addDisabled?: boolean
  navigationDisabled?: boolean
  onDelete?: () => void
  onComment?: () => void
  aiScore?: number
  children?: React.ReactNode
  stageOverride?: React.ReactNode
  onComposerTarget?: (element: HTMLDivElement | null) => void
  commentOverlayOpen?: boolean
}
export const ExecutionGallery: React.FC<IProps> = ({
  media,
  index,
  onChange,
  counts,
  isRtl,
  onAdd,
  addDisabled = false,
  navigationDisabled = false,
  onDelete,
  onComment,
  aiScore,
  children,
  stageOverride,
  onComposerTarget,
  commentOverlayOpen = false,
}) => {
  const t = useExecutionText()
  const [preview, setPreview] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const previewOpenRef = useRef(false)
  const downloadRef = useRef<AbortController | null>(null)
  const toast = useToast()
  const videoRef = useRef<HTMLVideoElement>(null)
  const touchStart = useRef<number | null>(null)
  const expandRef = useRef<HTMLButtonElement>(null)
  const thumbnailsRef = useRef<HTMLDivElement>(null)
  const selectedThumbnailRef = useRef<HTMLButtonElement>(null)
  const url = media[index]
  const scorePresentation = getAiScorePresentation(aiScore)

  const previous = () => {
    if (!navigationDisabled && index > 0) onChange(index - 1)
  }

  const next = () => {
    if (!navigationDisabled && index < media.length - 1) onChange(index + 1)
  }

  const openPreview = () => {
    previewOpenRef.current = true
    videoRef.current?.pause()
    setPreview(true)
  }

  const handleDownload = async () => {
    if (downloadRef.current || !url) return
    const controller = new AbortController()
    downloadRef.current = controller
    setDownloading(true)
    try {
      await downloadMedia(url, controller.signal)
    } catch {
      if (!controller.signal.aborted) {
        toast({ title: t.downloadFailed, status: 'error', duration: 4000, isClosable: true })
      }
    } finally {
      if (downloadRef.current === controller) {
        downloadRef.current = null
        setDownloading(false)
      }
    }
  }

  const handleKeys = (e: React.KeyboardEvent) => {
    if (previewOpenRef.current) return
    const target = e.target as HTMLElement
    if (target.matches('input, textarea, [contenteditable="true"], video')) return
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      isRtl ? next() : previous()
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault()
      isRtl ? previous() : next()
    }
  }

  const stage = (
    <div
      className="execution-gallery-stage"
      tabIndex={0}
      role="group"
      aria-label={`${t.file} ${index + 1} ${t.of} ${media.length}`}
      dir={isRtl ? 'rtl' : 'ltr'}
      onKeyDown={handleKeys}
      onTouchStart={(e) => {
        touchStart.current = e.touches[0].clientX
      }}
      onTouchEnd={(e) => {
        if (touchStart.current === null) return
        const diff = touchStart.current - e.changedTouches[0].clientX
        if (Math.abs(diff) > 45) {
          const towardNext = isRtl ? diff < 0 : diff > 0

          towardNext ? next() : previous()
        }
        touchStart.current = null
      }}
    >
      {videoMedia(url) ? (
        <video key={url} ref={videoRef} src={url} controls playsInline preload="metadata" />
      ) : (
        <img src={url} alt={`${t.file} ${index + 1}`} onDoubleClick={openPreview} />
      )}
      <span className="execution-file-label">
        {t.file}{' '}
        <bdi>
          {index + 1} / {media.length}
        </bdi>
        <span className="execution-file-name">
          {' '}
          · <bdi>{mediaName(url)}</bdi>
        </span>
      </span>
      <div className="execution-gallery-tools">
        <Button ref={expandRef} variant="icon" aria-label={t.fullscreen} title={t.fullscreen} onClick={openPreview}>
          <Maximize2 size={17} />
        </Button>
        <Button
          onClick={handleDownload}
          isLoading={downloading}
          isDisabled={downloading}
          variant="icon"
          aria-label={downloading ? t.downloading : t.download}
          title={downloading ? t.downloading : t.download}
        >
          <Download size={17} />
        </Button>
        {onDelete && (
          <Button variant="icon" onClick={onDelete} aria-label={t.delete} title={t.delete}>
            <Trash2 size={17} />
          </Button>
        )}
        {onComment && (
          <Button variant="icon" onClick={onComment} aria-label={t.commentOnMedia} title={t.commentOnMedia}>
            <MessageSquarePlus size={17} />
          </Button>
        )}
      </div>
      {index > 0 && (
        <Button
          variant="icon"
          className="execution-gallery-prev"
          aria-label={t.previous}
          isDisabled={navigationDisabled}
          onClick={previous}
        >
          {isRtl ? <ChevronRight /> : <ChevronLeft />}
        </Button>
      )}
      {index < media.length - 1 && (
        <Button
          variant="icon"
          className="execution-gallery-next"
          aria-label={t.next}
          isDisabled={navigationDisabled}
          onClick={next}
        >
          {isRtl ? <ChevronLeft /> : <ChevronRight />}
        </Button>
      )}
      {scorePresentation.score !== undefined && (
        <span
          className="execution-gallery-ai"
          style={{
            backgroundColor: scorePresentation.colors.bg,
            borderColor: scorePresentation.colors.border,
            color: scorePresentation.colors.text,
          }}
        >
          ✦ {t.score} <bdi>{scorePresentation.score}%</bdi>
        </span>
      )}
      {children}
    </div>
  )

  useLayoutEffect(() => {
    const viewport = thumbnailsRef.current
    const selected = selectedThumbnailRef.current
    if (!viewport || !selected) return
    const revealSelected = () => {
      if (viewport.scrollWidth <= viewport.clientWidth) return
      const bounds = viewport.getBoundingClientRect()
      const thumbnail = selected.getBoundingClientRect()
      // Keep the selected thumbnail clear of the 28px fade; physical bounds also work in RTL.
      const inset = 32
      const delta =
        thumbnail.left < bounds.left + inset
          ? thumbnail.left - bounds.left - inset
          : thumbnail.right > bounds.right - inset
          ? thumbnail.right - bounds.right + inset
          : 0
      if (delta) viewport.scrollBy({ left: delta })
    }
    revealSelected()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(revealSelected)
    observer?.observe(viewport)
    return () => observer?.disconnect()
  }, [index, media.length, isRtl])

  useEffect(() => {
    videoRef.current?.pause()
  }, [url])

  useEffect(
    () => () => {
      downloadRef.current?.abort()
      downloadRef.current = null
    },
    []
  )

  return (
    <section className="execution-gallery">
      {stageOverride || stage}
      {preview && (
        <ExecutionMediaPreview
          media={media}
          index={index}
          counts={counts}
          isRtl={isRtl}
          onChange={onChange}
          navigationDisabled={navigationDisabled}
          onComment={onComment}
          onDownload={handleDownload}
          downloading={downloading}
          onComposerTarget={onComposerTarget}
          commentOverlayOpen={commentOverlayOpen}
          onClose={() => {
            previewOpenRef.current = false
            setPreview(false)
            expandRef.current?.focus()
          }}
        />
      )}
      <HorizontalScroller
        className="execution-thumbnails"
        gap="var(--execution-thumbnail-gap, 8px)"
        fadeSize={28}
        scrollRef={thumbnailsRef}
      >
        {media.map((item, i) => (
          <Button
            key={item}
            ref={i === index ? selectedThumbnailRef : undefined}
            variant="ghost"
            className={`execution-thumbnail ${i === index ? 'is-selected' : ''}`}
            aria-label={`${t.file} ${i + 1} · ${counts[i] || 0} ${t.comments}`}
            aria-pressed={i === index}
            isDisabled={navigationDisabled}
            onClick={() => onChange(i)}
          >
            <MediaThumbnail url={item} />
            {!!counts[i] && <span className="execution-thumbnail-count">{counts[i]}</span>}
            {videoMedia(item) && <Video size={12} className="execution-thumbnail-video" />}
          </Button>
        ))}
        {onAdd && (
          <Button
            variant="ghost"
            className="execution-add-file"
            onClick={onAdd}
            isDisabled={addDisabled}
            aria-label={t.add}
          >
            <Plus size={16} />
            <span>{t.add}</span>
          </Button>
        )}
      </HorizontalScroller>
    </section>
  )
}
